/**
 * What a benchmark page posts to the dev server when a run ends (P2.0-T8, R-0152), shared so the page builds what the
 * server checks: the report's text exactly as "Copy results" copies it, and a small envelope saying when the run
 * ended, the page's query, the browser, the screen, the renderer, the build and an optional machine label. The label
 * is the only part of a submission that reaches a file name, so its rule is narrow: letters, digits, dash and
 * underscore, at most 32. The query is saved with any token's value blanked, since a development link may carry one.
 */

/**
 * The longest machine label.
 */
export const BENCH_LABEL_MAX = 32;

/**
 * The largest body a submission may be, in bytes.
 */
export const BENCH_SUBMISSION_MAX_BYTES = 64 * 1024;

/**
 * The bounds of the envelope's fields, which the page cuts its values to and the server checks.
 */
export const BENCH_ENVELOPE_BOUNDS = { query: 2048, userAgent: 512, renderer: 256, build: 64, when: 40, screen: 65_535, pixelRatio: 16 } as const;

const LABEL = /^[A-Za-z0-9_-]{1,32}$/;
const ISO_DATE = /^\d{4}-\d{2}-\d{2}T/;
const SECRET_PARAMETERS: ReadonlySet<string> = new Set(["token", "refresh"]);
const REDACTED = "redacted";
const NAMED_KEY_MAX = 40;
const ENVELOPE_KEYS: ReadonlySet<string> = new Set(["when", "query", "userAgent", "screen", "devicePixelRatio", "renderer", "build", "label"]);
const BODY_KEYS: ReadonlySet<string> = new Set(["envelope", "text"]);

/**
 * The envelope beside a report's text.
 */
export interface BenchEnvelope {
  /** When the run ended on the page's clock, ISO 8601. */
  readonly when: string;
  /** The page's URL query, a token's value blanked. */
  readonly query: string;
  readonly userAgent: string;
  readonly screen: { readonly width: number; readonly height: number };
  readonly devicePixelRatio: number;
  /** The GPU renderer the benchmark read, or null when the browser gave none. */
  readonly renderer: string | null;
  /** The commit the page was built from, as the page knows it. */
  readonly build: string;
  /** The machine label, or null. */
  readonly label: string | null;
}

/**
 * A submission: the envelope and the report's text.
 */
export interface BenchSubmission {
  readonly envelope: BenchEnvelope;
  readonly text: string;
}

/**
 * A parsed submission, or why it was refused.
 */
export type BenchSubmissionResult = { readonly ok: true; readonly submission: BenchSubmission } | { readonly ok: false; readonly message: string };

/**
 * The machine label a value gives.
 *
 * @param value What a link, a text box or a stored setting holds.
 * @returns The value when it is letters, digits, dash and underscore, 1 to 32 of them; otherwise null.
 * @example
 * benchLabel("macbook-2015"); // "macbook-2015"
 * benchLabel("Vivobook battery"); // null
 */
export function benchLabel(value: string | null | undefined): string | null {
  return typeof value === "string" && LABEL.test(value) ? value : null;
}

/**
 * A query parameter's name as a page reads it: percent-decoded, a plus read as a space, in lower case.
 *
 * @param name The name as the query writes it.
 * @returns The decoded name, or the name as written when it does not decode.
 */
function parameterName(name: string): string {
  try {
    return decodeURIComponent(name.replace(/\+/g, " ")).toLowerCase();
  } catch {
    return name.toLowerCase();
  }
}

/**
 * A page's URL query with the value of every `token` and `refresh` parameter blanked, whatever their case and however
 * their names are percent-encoded; each name stays as it was written.
 *
 * @param search The query, with or without its leading `?`.
 * @returns The query with a leading `?`, or an empty string for an empty query.
 */
export function redactQuery(search: string): string {
  const body = search.startsWith("?") ? search.slice(1) : search;
  if (body === "") {
    return "";
  }
  const parts = body.split("&").map((part) => {
    const cut = part.indexOf("=");
    const name = cut < 0 ? part : part.slice(0, cut);
    return SECRET_PARAMETERS.has(parameterName(name)) ? `${name}=${REDACTED}` : part;
  });
  return `?${parts.join("&")}`;
}

/**
 * Whether a value is text within a length.
 *
 * @param value The value.
 * @param max The most characters.
 * @returns True for such text.
 */
function isText(value: unknown, max: number): value is string {
  return typeof value === "string" && value.length <= max;
}

/**
 * Whether a value is a plain record.
 *
 * @param value The value.
 * @returns True for an object that is not an array.
 */
function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/**
 * Whether a value is a screen dimension.
 *
 * @param value The value.
 * @returns True for a whole number from 1 to 65,535.
 */
function isDimension(value: unknown): value is number {
  return typeof value === "number" && Number.isInteger(value) && value >= 1 && value <= BENCH_ENVELOPE_BOUNDS.screen;
}

/**
 * The first key of a record outside a set, cut to a length fit for a message.
 *
 * @param record The record.
 * @param known The keys it may have.
 * @returns The key, or null.
 */
function unknownKey(record: Record<string, unknown>, known: ReadonlySet<string>): string | null {
  return Object.keys(record).find((key) => !known.has(key))?.slice(0, NAMED_KEY_MAX) ?? null;
}

/**
 * Checks an envelope, naming the first field out of its bounds.
 *
 * @param envelope The envelope's record.
 * @returns The envelope, its query redacted and an absent label read as null, or the message.
 */
function parseEnvelope(envelope: Record<string, unknown>): BenchEnvelope | string {
  const bounds = BENCH_ENVELOPE_BOUNDS;
  const extra = unknownKey(envelope, ENVELOPE_KEYS);
  if (extra !== null) {
    return `envelope has an unknown field: ${extra}`;
  }
  const { when, query, userAgent, screen, devicePixelRatio, renderer, build, label } = envelope;
  if (!isText(when, bounds.when) || !ISO_DATE.test(when) || Number.isNaN(Date.parse(when))) {
    return "envelope.when must be an ISO 8601 time";
  }
  if (!isText(query, bounds.query)) {
    return `envelope.query must be text of at most ${bounds.query} characters`;
  }
  if (!isText(userAgent, bounds.userAgent)) {
    return `envelope.userAgent must be text of at most ${bounds.userAgent} characters`;
  }
  if (!isRecord(screen) || Object.keys(screen).length !== 2 || !isDimension(screen["width"]) || !isDimension(screen["height"])) {
    return `envelope.screen must be a width and a height in whole pixels from 1 to ${bounds.screen}`;
  }
  if (typeof devicePixelRatio !== "number" || !(devicePixelRatio > 0) || devicePixelRatio > bounds.pixelRatio) {
    return `envelope.devicePixelRatio must be a number over 0 and at most ${bounds.pixelRatio}`;
  }
  if (renderer !== null && !isText(renderer, bounds.renderer)) {
    return `envelope.renderer must be null or text of at most ${bounds.renderer} characters`;
  }
  if (!isText(build, bounds.build)) {
    return `envelope.build must be text of at most ${bounds.build} characters`;
  }
  if (label !== undefined && label !== null && (typeof label !== "string" || benchLabel(label) === null)) {
    return `envelope.label must be null or letters, digits, dash and underscore, at most ${BENCH_LABEL_MAX}`;
  }
  return { when, query: redactQuery(query), userAgent, screen: { width: screen["width"], height: screen["height"] }, devicePixelRatio, renderer, build, label: label ?? null };
}

/**
 * Checks a posted body against the submission's schema: an object with exactly `envelope` and `text`, every envelope
 * field within its bounds and none besides. Nothing in it is trusted further than this: the text stays text.
 *
 * @param value The parsed JSON body.
 * @returns The submission, its query redacted and an absent label read as null, or the refusal's message.
 */
export function parseBenchSubmission(value: unknown): BenchSubmissionResult {
  if (!isRecord(value)) {
    return { ok: false, message: "the body must be an object with envelope and text" };
  }
  const extra = unknownKey(value, BODY_KEYS);
  if (extra !== null) {
    return { ok: false, message: `the body has an unknown field: ${extra}` };
  }
  const { envelope, text } = value;
  if (!isRecord(envelope)) {
    return { ok: false, message: "envelope must be an object" };
  }
  if (typeof text !== "string" || text === "") {
    return { ok: false, message: "text must be the report's text, not empty" };
  }
  const parsed = parseEnvelope(envelope);
  return typeof parsed === "string" ? { ok: false, message: parsed } : { ok: true, submission: { envelope: parsed, text } };
}
